import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, ExternalLink, Loader2 } from 'lucide-react';
import PatientPortalShell from '../components/PatientPortalShell';
import ReferralStatusBadge from '../components/ReferralStatusBadge';
import { referralPortalApi } from '../api/referralPortal.api';

export default function PatientReferralDetailPage() {
  const { id } = useParams();
  const [me, setMe] = useState(null);
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([referralPortalApi.me(), referralPortalApi.getRequest(id)])
      .then(([m, r]) => { setMe(m); setRow(r); })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading || !row) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#F8F4F0]">
        <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
      </div>
    );
  }

  const branding = me?.tenant?.branding || {};
  const issued = row.status === 'issued';

  return (
    <PatientPortalShell branding={branding} tenantName={me?.tenant?.name}>
      <Link to="/patient/referrals" className="mb-4 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800">
        <ArrowLeft className="h-4 w-4" /> Back to requests
      </Link>
      <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-stone-400">Referral request</p>
            <h2 className="mt-1 font-heading text-3xl text-stone-900">{row.request_number}</h2>
            <div className="mt-2 flex flex-wrap gap-2">
              <ReferralStatusBadge status={row.status} />
              <span className="rounded-full border border-stone-200 px-2.5 py-0.5 text-xs font-semibold text-stone-600">
                {row.station_type === 'out_station' ? 'Out-station' : 'In-station'}
              </span>
              {row.needs_credit_letter ? (
                <span className="rounded-full border border-sky-200 bg-sky-50 px-2.5 py-0.5 text-xs font-semibold text-sky-900">
                  Credit letter requested
                </span>
              ) : null}
            </div>
          </div>
          {issued && (
            <div className="flex flex-wrap gap-2">
              <a
                href={referralPortalApi.patientLetterPreviewUrl(row.id)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-stone-200 px-4 py-2 text-sm font-medium hover:bg-stone-50"
              >
                <ExternalLink className="h-4 w-4" /> Print preview
              </a>
              <a
                href={referralPortalApi.patientLetterPdfUrl(row.id)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white"
                style={{ background: branding.primary_color || '#F15A22' }}
              >
                <Download className="h-4 w-4" /> Download PDF
              </a>
            </div>
          )}
        </div>

        {row.fic_approval_required && (
          <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            Approval: <strong>{row.fic_status}</strong>
            {row.fic_stage && row.fic_stage !== 'not_required' && row.fic_stage !== 'complete'
              ? ` · Stage: ${String(row.fic_stage).replace(/_/g, ' ')}`
              : ''}
            {row.fic_note ? ` — ${row.fic_note}` : ''}
          </div>
        )}

        {issued && (
          <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950">
            Referral letter issued. Use <strong>Print preview</strong> or <strong>Download PDF</strong> above.
          </div>
        )}

        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <Block title="Patient">
            <Line label="Name" value={row.patient?.name} />
            <Line label="Relationship" value={row.patient?.relationship} />
            <Line label="Phone" value={row.patient?.phone} />
            <Line label="Age / Gender" value={`${row.patient?.age || '—'} / ${row.patient?.gender || '—'}`} />
            <Line label="MRN" value={row.patient?.mrn || '—'} />
          </Block>
          <Block title="Seeking referral">
            <Line label="Hospital / Clinic" value={row.seeking?.hospital_clinic} />
            <Line label="Specialisation" value={row.seeking?.specialisation} />
            <Line label="City" value={row.seeking?.city || '—'} />
            <Line label="Credit letter" value={row.needs_credit_letter ? 'Requested' : 'Not required'} />
            <Line label="Reason" value={row.seeking?.reason} />
          </Block>
        </div>
        {row.reviewer_notes && (
          <div className="mt-6 rounded-2xl bg-stone-50 px-4 py-3 text-sm text-stone-700">
            <strong>Notes:</strong> {row.reviewer_notes}
          </div>
        )}
      </div>
    </PatientPortalShell>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <h3 className="mb-3 font-heading text-lg text-stone-900">{title}</h3>
      <dl className="space-y-2 text-sm">{children}</dl>
    </div>
  );
}

function Line({ label, value }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2">
      <dt className="text-stone-400">{label}</dt>
      <dd className="font-medium text-stone-800">{value}</dd>
    </div>
  );
}
